import { FieldTypeSelector } from '@monorepo/shared/componentsV2/fields/FieldTypeSelector';
import { ModalWizard } from '@monorepo/shared/componentsV2/modalWizard/modalWizard';
import { USAGE_TRACKING_PREFIX } from '@monorepo/shared/constants/usageTracking';
import { FormTemplateFieldTypes } from 'mapistry-shared';
import React, { useCallback, useMemo } from 'react';
import { useFormTemplateContext } from '../../EditFormTemplatePage/FormTemplateContext';
import { addableFormTemplateFieldTypes } from '../AddFieldModal/addableFormTemplateFieldTypes';
import { getFieldTypeDisplay } from '../getFieldTypeDisplay';
import { getFieldCreateRequest } from '../hooks/useEditableFormTemplateState/newFieldHelpers';
import {
  CreateFormTemplateFieldGroupFormValues,
  CreateFormTemplateFieldRequestValues,
} from '../types';
import { EditNewFieldInNewSectionWizardPage } from './EditNewFieldInNewSectionModal';
import { EditSectionForm } from './EditSectionForm';

type CreateGroupFormValues = {
  group: CreateFormTemplateFieldGroupFormValues;
  field: CreateFormTemplateFieldRequestValues;
};

export type AddSectionModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const FIELD_PREFIX = 'field';
const GROUP_PREFIX = 'group';

export function AddSectionModal({ isOpen, onClose }: AddSectionModalProps) {
  const {
    formTemplateHelpers: { addGroup },
  } = useFormTemplateContext();

  const handleSubmit = useCallback(
    async (sectionFormValues: CreateGroupFormValues) => {
      const isDuplicable = !!sectionFormValues.group.isDuplicable;
      const newGroup = {
        ...sectionFormValues.group,
        isDuplicable,
        isStencilProtected:
          isDuplicable && !!sectionFormValues.group.isStencilProtected,
      };
      addGroup(newGroup, [getFieldCreateRequest(sectionFormValues.field)]);
    },
    [addGroup],
  );

  const wizardPages = useMemo(
    () => [
      <EditSectionForm prefix={GROUP_PREFIX} />,
      <FieldTypeSelector<FormTemplateFieldTypes>
        availableFieldTypes={addableFormTemplateFieldTypes}
        getFieldTypeDisplay={getFieldTypeDisplay}
        instructions="Add your first field to the new section."
        prefix={FIELD_PREFIX}
        rowCount={7}
      />,
      <EditNewFieldInNewSectionWizardPage
        groupPrefix={GROUP_PREFIX}
        fieldPrefix={FIELD_PREFIX}
      />,
    ],
    [],
  );

  if (!isOpen) {
    return null;
  }

  return (
    <ModalWizard<CreateGroupFormValues>
      isEditing={false}
      itemLabel="section"
      modalId={`${USAGE_TRACKING_PREFIX}-add-form-template-section-modal`}
      onClose={onClose}
      onPageSubmit={handleSubmit}
      title="Create new section"
      wizardPages={wizardPages}
      completionButtonText="Apply"
      showBackButton
    />
  );
}
